import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { ArrowLeft, Loader2, Pencil, Power, Upload, Trash2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import {
  getMyHome, getMyDeviceMetrics, getMyDeviceCost, getMyDeviceRawReadings, updateMyDevice, uploadMyDeviceFile,
  getMyVendors, eraseMyDeviceHistory, reprocessMyDeviceHistory, getMyInvoices,
  type MyDevice, type MyDeviceMetric, type MyDeviceCost, type MyRawReading, type MyVendor,
} from '../api/energy-os'
import { energyOsClient } from '../api/client'
import { useDateFmt, applyDateFormat } from '../hooks/useDateFmt'
import { Modal } from '../components/Modal'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'

function toWebSocketUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

// Power arrives as kW under either metric name (mock's 'power', Shelly's 'powerKw') — displayed
// in Watts here since that's the unit homeowners actually think in for a single appliance.
function isPowerMetric(metric: string): boolean {
  return metric === 'power' || metric === 'powerKw'
}

// segmentLabel/segmentColor (Phase 9) — which contract price segment applied to this reading,
// server-computed (see routes.ts's /metrics and /cost handlers) — absent when the home has no
// contract, in which case every chart below renders exactly as it always has.
interface ChartPoint { timestamp: string; value: number; segmentLabel?: string; segmentColor?: string }
interface MetricSeries { id: string; title: string; unit: string; color: string; rows: ChartPoint[] }

const SERIES_COLORS = ['#465fff', '#f79009', '#12b76a', '#7a5af8', '#f04438']

// Fixed display order regardless of which metric happens to have the chronologically-earliest
// surviving reading at render time — relying on Map/array insertion order here (the previous
// approach) meant the order silently flipped (swapping which chart section appeared first)
// whenever the live WebSocket feed or a periodic refetch changed which point was earliest,
// with no action from the user.
const METRIC_DISPLAY_PRIORITY: Record<string, number> = { power: 0, powerKw: 0, voltage: 1, soc: 2 }
function metricSortKey(metricName: string): [number, string] {
  return [METRIC_DISPLAY_PRIORITY[metricName] ?? 99, metricName]
}

// One series per distinct Silver metric name — a device reporting both power and voltage (a
// Shelly appliance) or power and soc (the mock battery) gets one independent chart per metric
// instead of one combined multi-axis chart, per the user's own request.
function buildSilverSeries(metrics: MyDeviceMetric[], deviceType: string): MetricSeries[] {
  const byMetric = new Map<string, MyDeviceMetric[]>()
  for (const m of metrics) {
    const list = byMetric.get(m.metric) ?? []
    list.push(m)
    byMetric.set(m.metric, list)
  }
  const orderedMetricNames = [...byMetric.keys()].sort((a, b) => {
    const [priorityA, nameA] = metricSortKey(a)
    const [priorityB, nameB] = metricSortKey(b)
    return priorityA !== priorityB ? priorityA - priorityB : nameA.localeCompare(nameB)
  })

  const series: MetricSeries[] = []
  let colorIdx = 0
  for (const metricName of orderedMetricNames) {
    const readings = byMetric.get(metricName)!
    let title = metricName
    let unit = readings[0]?.unit ?? ''
    let transform = (v: number) => v
    if (isPowerMetric(metricName)) {
      title = deviceType === 'solar' ? 'Watt production' : deviceType === 'battery' ? 'Power' : 'Watt consumption'
      unit = 'W'
      transform = (v) => Math.round(v * 1000 * 100) / 100
    } else if (metricName === 'voltage') {
      title = 'Voltage'; unit = 'V'
    } else if (metricName === 'soc') {
      title = 'Charging level'; unit = '%'
    }
    series.push({
      id: `silver-${metricName}`, title, unit, color: SERIES_COLORS[colorIdx++ % SERIES_COLORS.length],
      rows: readings.map((r) => ({ timestamp: r.timestamp, value: transform(r.value), segmentLabel: r.segmentLabel, segmentColor: r.segmentColor })),
    })
  }
  return series
}

// camelCase metricType (e.g. "costOfUsage") -> "Cost of usage" for a readable chart title.
function prettifyMetricType(metricType: string): string {
  const spaced = metricType.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// One series per distinct Gold metricType — today that's just 'costOfUsage', but this stays
// generic for any future Gold calculation without needing chart-code changes.
function buildGoldSeries(costs: MyDeviceCost[]): MetricSeries[] {
  const byType = new Map<string, MyDeviceCost[]>()
  for (const c of costs) {
    const list = byType.get(c.metricType) ?? []
    list.push(c)
    byType.set(c.metricType, list)
  }

  const series: MetricSeries[] = []
  let colorIdx = 0
  for (const [metricType, rows] of byType) {
    series.push({
      id: `gold-${metricType}`, title: prettifyMetricType(metricType), unit: rows[0]?.unit ?? '',
      color: SERIES_COLORS[colorIdx++ % SERIES_COLORS.length],
      rows: rows.map((c) => ({ timestamp: c.periodEnd, value: c.value, segmentLabel: c.segmentLabel, segmentColor: c.segmentColor })),
    })
  }
  return series
}

// Range selector — filters the already-fetched metric history client-side (the backend has no
// date-range query param; it returns everything it still has). Note this is bounded by
// telemetry-poller.service.ts's own retention (100 Metric readings kept per device), so "Max"
// shows whatever survives that pruning, not necessarily the device's entire lifetime history.
type RangeKey = '1d' | '5d' | '1m' | '1y' | '5y' | 'max'
const RANGE_OPTIONS: { key: RangeKey; label: string }[] = [
  { key: '1d', label: '1d' }, { key: '5d', label: '5d' }, { key: '1m', label: '1m' },
  { key: '1y', label: '1y' }, { key: '5y', label: '5y' }, { key: 'max', label: 'Max' },
]
const RANGE_DAYS: Record<RangeKey, number> = { '1d': 1, '5d': 5, '1m': 30, '1y': 365, '5y': 365 * 5, max: Infinity }

// Short, zoom-appropriate axis-tick labels (independent of the business's configured full
// DATE_FORMAT, which is reserved for the tooltip's exact-moment display) — a 5-year view showing
// full timestamps down to the second would be unreadable. Once an aggregation level is picked,
// that always wins over the range-based guess — a "per month" chart should show month ticks no
// matter how wide the selected range is.
function tickFormat(range: RangeKey, aggregation: AggregationLevel): string {
  if (aggregation === 'hour') return 'MM-dd HH:ii'
  if (aggregation === 'day') return 'MM-dd'
  if (aggregation === 'month') return 'YYYY-MM'
  if (aggregation === 'year') return 'YYYY'
  if (range === '1d') return 'HH:ii'
  if (range === '5d') return 'MM-dd HH:ii'
  if (range === '1m') return 'MM-dd'
  return 'YYYY-MM'
}

// Live, client-side-only aggregation (Phase 9) — no server round-trip, operates on whatever's
// already been fetched for the chart. 'real' is a passthrough (today's exact behavior). Every
// other level buckets consecutive readings by truncating their ISO timestamp to the bucket's own
// start instant, then either SUMS (for a rate-like unit, integrating rate x hours-elapsed between
// readings — the same formula and 1h cap monthly-consumption.service.ts uses server-side for
// Silver's own kWh accumulation, just mirrored here since the user wants this to stay live/
// unstored rather than round-tripping to the backend) or AVERAGES (for a plain instantaneous
// quantity like voltage/SoC, where "total" has no meaning).
type AggregationLevel = 'real' | 'hour' | 'day' | 'month' | 'year'
const AGGREGATION_OPTIONS: { key: AggregationLevel; label: string }[] = [
  { key: 'real', label: 'Real' }, { key: 'hour', label: 'Hour' }, { key: 'day', label: 'Day' },
  { key: 'month', label: 'Month' }, { key: 'year', label: 'Year' },
]
const MAX_INTERVAL_HOURS = 1

function bucketStartIso(timestamp: string, level: AggregationLevel): string {
  if (level === 'hour') return `${timestamp.slice(0, 13)}:00:00.000Z`
  if (level === 'day') return `${timestamp.slice(0, 10)}T00:00:00.000Z`
  if (level === 'month') return `${timestamp.slice(0, 7)}-01T00:00:00.000Z`
  return `${timestamp.slice(0, 4)}-01-01T00:00:00.000Z`
}

// A rate-like unit (W, kW, €/h) integrates into a total (Wh->kWh, €) over the interval between
// two consecutive readings; anything else (V, %, ...) has no "total" and gets averaged instead.
function aggregatedUnit(unit: string, level: AggregationLevel): string {
  if (level === 'real') return unit
  if (unit === 'W' || unit === 'kW') return 'kWh'
  if (unit === '€/h') return '€'
  return unit
}

// Segment-aware (Phase 9): buckets by (bucket start, segmentLabel) instead of just bucket start,
// so a day that spans two price periods produces two summed points for that one day — exactly
// the input a stacked bar / multi-color line needs. Without segment data (no contract), every row
// shares the same (absent) segment key and this collapses back to one point per bucket, unchanged
// from before Phase 9.
function aggregateSeries(rows: ChartPoint[], level: AggregationLevel, unit: string): ChartPoint[] {
  if (level === 'real' || rows.length === 0) return rows
  const sorted = [...rows].sort((a, b) => a.timestamp.localeCompare(b.timestamp))

  if (unit === 'W' || unit === 'kW' || unit === '€/h') {
    const wattsToKw = unit === 'W' ? 1 / 1000 : 1
    const sums = new Map<string, ChartPoint>()
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1]
      const hoursGap = (new Date(sorted[i].timestamp).getTime() - new Date(prev.timestamp).getTime()) / 3_600_000
      const amount = prev.value * wattsToKw * Math.max(0, Math.min(hoursGap, MAX_INTERVAL_HOURS))
      const timestamp = bucketStartIso(prev.timestamp, level)
      const mapKey = `${timestamp}|${prev.segmentLabel ?? ''}`
      const existing = sums.get(mapKey)
      if (existing) existing.value += amount
      else sums.set(mapKey, { timestamp, value: amount, segmentLabel: prev.segmentLabel, segmentColor: prev.segmentColor })
    }
    return [...sums.values()]
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .map((p) => ({ ...p, value: Math.round(p.value * 1000) / 1000 }))
  }

  const groups = new Map<string, { timestamp: string; values: number[]; segmentLabel?: string; segmentColor?: string }>()
  for (const r of sorted) {
    const timestamp = bucketStartIso(r.timestamp, level)
    const mapKey = `${timestamp}|${r.segmentLabel ?? ''}`
    const existing = groups.get(mapKey)
    if (existing) existing.values.push(r.value)
    else groups.set(mapKey, { timestamp, values: [r.value], segmentLabel: r.segmentLabel, segmentColor: r.segmentColor })
  }
  return [...groups.values()]
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    .map((g) => ({
      timestamp: g.timestamp, segmentLabel: g.segmentLabel, segmentColor: g.segmentColor,
      value: Math.round((g.values.reduce((s, v) => s + v, 0) / g.values.length) * 100) / 100,
    }))
}

interface ChartSegment { key: string; label: string; color: string }
interface WideChartRow { timestamp: string; [segmentKey: string]: string | number | undefined }

// Reshapes a (possibly segment-labeled) flat point list into one row per timestamp with one
// column per distinct segment — the "wide" format Recharts needs to draw either a stacked bar
// (one <Bar> per segment sharing a stackId) or a multi-color line/area (one <Line>/<Area> per
// segment, each only populated where that segment applies). No segment data at all (no contract)
// collapses to a single 'value' column, matching every chart's pre-Phase-9 shape exactly.
function toWideChartRows(rows: ChartPoint[], fallbackColor: string): { data: WideChartRow[]; segments: ChartSegment[] } {
  const hasSegments = rows.some((r) => r.segmentLabel)
  if (!hasSegments) {
    return {
      data: rows.map((r) => ({ timestamp: r.timestamp, value: r.value })),
      segments: [{ key: 'value', label: '', color: fallbackColor }],
    }
  }

  const segmentOrder: string[] = []
  const segmentByLabel = new Map<string, ChartSegment>()
  for (const r of rows) {
    if (r.segmentLabel && !segmentByLabel.has(r.segmentLabel)) {
      const key = `seg_${segmentOrder.length}`
      segmentOrder.push(r.segmentLabel)
      segmentByLabel.set(r.segmentLabel, { key, label: r.segmentLabel, color: r.segmentColor ?? '#98a2b3' })
    }
  }

  const byTimestamp = new Map<string, WideChartRow>()
  for (const r of rows) {
    let row = byTimestamp.get(r.timestamp)
    if (!row) { row = { timestamp: r.timestamp }; byTimestamp.set(r.timestamp, row) }
    const segment = r.segmentLabel ? segmentByLabel.get(r.segmentLabel) : undefined
    row[segment?.key ?? 'value'] = r.value
  }

  return {
    data: [...byTimestamp.values()].sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
    segments: segmentOrder.map((label) => segmentByLabel.get(label)!),
  }
}

type PipelineTab = 'bronze' | 'silver' | 'gold'
type SelectedRow = { title: string; entries: Array<[string, string]>; rawPayload?: string } | null

export function EnergyOSDeviceDetail() {
  const { id } = useParams<{ id: string }>()
  const fmt = useDateFmt()
  const queryClient = useQueryClient()
  const wsRef = useRef<WebSocket | null>(null)
  const [pipelineTab, setPipelineTab] = useState<PipelineTab>('bronze')
  const [showEdit, setShowEdit] = useState(false)
  const [range, setRange] = useState<RangeKey>('1m')
  const [aggregation, setAggregation] = useState<AggregationLevel>('real')
  const [chartMode, setChartMode] = useState<'bar' | 'line' | 'area'>('bar')
  const [selectedRow, setSelectedRow] = useState<SelectedRow>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileUploadError, setFileUploadError] = useState('')

  const { data: home } = useQuery({
    queryKey: ['my-home'],
    queryFn: () => getMyHome().then((r) => r.data),
    retry: false,
  })
  const device = home?.devices.find((d) => d.deviceCoffeeId === id)

  const { data: vendors } = useQuery({
    queryKey: ['my-vendors'],
    queryFn: () => getMyVendors().then((r) => r.data),
    staleTime: 5 * 60_000, // rarely changes — see bootstrap.service.ts's VENDOR_SEEDS
  })
  const vendorConfig = vendors?.find((v) => v.name === device?.vendor)

  // Which vendors even show this control, and what happens on upload, is entirely server-driven
  // from the device's own vendor config (see rest/routes.ts's file-upload route) — this check is
  // client-side UX only, not the real gate. Bronze/Silver/Gold ingestion runs async in Brainer
  // after upload, so a successful response here just means the file was accepted, not that
  // readings have appeared yet — invalidating the raw-readings query lets the Bronze tab pick
  // them up on its next poll rather than promising an instant chart update.
  const uploadFile = useMutation({
    mutationFn: (file: File) => uploadMyDeviceFile(id!, file),
    onSuccess: () => {
      setFileUploadError('')
      queryClient.invalidateQueries({ queryKey: ['my-device-raw', id] })
    },
    onError: (err: any) => {
      setFileUploadError(err?.response?.data?.error || 'Upload failed')
    },
  })

  // Fire-and-forget, same as reprocess below: a device's history can run into the thousands of
  // rows, paced at 1200ms/delete server-side — an awaited response would take far longer than any
  // reasonable HTTP timeout, so this only confirms the erase started, not its outcome.
  const [eraseError, setEraseError] = useState('')
  const eraseHistory = useMutation({
    mutationFn: () => eraseMyDeviceHistory(id!),
    onSuccess: () => {
      setEraseError('')
      queryClient.invalidateQueries({ queryKey: ['my-device-raw', id] })
      queryClient.invalidateQueries({ queryKey: ['my-device-metrics', id] })
      queryClient.invalidateQueries({ queryKey: ['my-device-cost', id] })
    },
    onError: (err: any) => {
      setEraseError(err?.response?.data?.error || 'Failed to erase history')
    },
  })
  const handleEraseHistory = () => {
    if (!window.confirm('This permanently deletes all raw, silver, and gold history for this device. This cannot be undone. Continue?')) return
    eraseHistory.mutate()
  }

  // Recovers Silver Metrics that never got written from Bronze — e.g. a device's vendor had no
  // adapter yet at upload time, or a large batch was rate-limited partway through. Safe to run
  // any time: it skips (metric, timestamp) pairs that already have a Metric.
  const [reprocessError, setReprocessError] = useState('')
  const reprocessHistory = useMutation({
    mutationFn: () => reprocessMyDeviceHistory(id!),
    onSuccess: () => {
      setReprocessError('')
      queryClient.invalidateQueries({ queryKey: ['my-device-metrics', id] })
      queryClient.invalidateQueries({ queryKey: ['my-device-cost', id] })
    },
    onError: (err: any) => {
      setReprocessError(err?.response?.data?.error || 'Failed to reprocess history')
    },
  })

  // Brainer's children listing orders by coffee id, not by timestamp — sort chronologically so
  // the charts' x-axis (and the tables) read in a sensible order. Merges each fresh REST fetch
  // with whatever's already cached (deduped by timestamp+metric) instead of replacing it outright
  // — the live WS subscription below writes into this same cache in real time, and a blind
  // replace on the next 30s poll could momentarily drop a point WS already delivered (the Brainer
  // write behind a just-broadcast metric isn't guaranteed to be read-visible yet), which is what
  // made the chart appear to jump/move on its own with no user action.
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['my-device-metrics', id],
    queryFn: async () => {
      const fresh = (await getMyDeviceMetrics(id!)).data
      const prev = queryClient.getQueryData<MyDeviceMetric[]>(['my-device-metrics', id]) ?? []
      const byKey = new Map<string, MyDeviceMetric>()
      for (const m of [...prev, ...fresh]) byKey.set(`${m.timestamp}|${m.metric}`, m)
      return [...byKey.values()].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    },
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const { data: costs } = useQuery({
    queryKey: ['my-device-cost', id],
    queryFn: () => getMyDeviceCost(id!).then((r) => [...r.data].sort((a, b) => a.periodStart.localeCompare(b.periodStart))),
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const { data: rawReadings } = useQuery({
    queryKey: ['my-device-raw', id],
    queryFn: () => getMyDeviceRawReadings(id!).then((r) => r.data),
    enabled: !!id,
    refetchInterval: 30_000,
  })

  const silverSeries = useMemo(() => buildSilverSeries(metrics ?? [], device?.deviceType ?? ''), [metrics, device?.deviceType])
  const goldSeries = useMemo(() => buildGoldSeries(costs ?? []), [costs])
  const allSeries = [...silverSeries, ...goldSeries]

  function visibleRows(rows: ChartPoint[]): ChartPoint[] {
    if (range === 'max') return rows
    const cutoff = Date.now() - RANGE_DAYS[range] * 86_400_000
    return rows.filter((r) => new Date(r.timestamp).getTime() >= cutoff)
  }

  // True push updates, not just the 30s poll above (kept as a resync backstop) — one connection
  // per page visit, subscribed to this one device's live Silver-write stream.
  useEffect(() => {
    if (!id) return
    const token = localStorage.getItem('accessToken')
    if (!token) return
    const base = toWebSocketUrl(energyOsClient.defaults.baseURL ?? '')
    const ws = new WebSocket(`${base}/ws/dashboard?token=${token}`)
    wsRef.current = ws

    ws.onopen = () => ws.send(JSON.stringify({ type: 'subscribe', deviceId: id }))
    ws.onmessage = (event) => {
      let msg: any
      try { msg = JSON.parse(event.data) } catch { return }
      if (msg?.type !== 'metric' || msg.deviceId !== id) return
      const incoming: MyDeviceMetric = msg.metric
      // Merge in, don't truncate — a fixed small slice here used to nuke whatever multi-day/
      // month/year history the REST fetch had already loaded down to a couple hundred combined
      // points the instant a single live push arrived, which is what made a wide range view
      // (5d/1m/Max) intermittently collapse to the last few minutes with no user action. The
      // 30s REST poll's own query already merges+dedupes the full, unbounded device history
      // (see its queryFn above), so this only needs to keep that cache current between polls,
      // the same key convention (`timestamp|metric`) that poll already uses.
      queryClient.setQueryData<MyDeviceMetric[]>(['my-device-metrics', id], (prev = []) => {
        const byKey = new Map(prev.map((m) => [`${m.timestamp}|${m.metric}`, m]))
        byKey.set(`${incoming.timestamp}|${incoming.metric}`, incoming)
        return [...byKey.values()].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      })
    }

    return () => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'unsubscribe', deviceId: id }))
      ws.close()
      wsRef.current = null
    }
  }, [id, queryClient])

  function openRawRow(r: MyRawReading) {
    setSelectedRow({
      title: 'Bronze reading',
      entries: [
        ['Received', fmt(r.receivedAt)],
        ['Ingest mode', r.ingestMode],
        ['Source timestamp', r.sourceTimestamp ? fmt(r.sourceTimestamp) : '—'],
      ],
      rawPayload: r.rawPayload,
    })
  }
  function openMetricRow(m: MyDeviceMetric) {
    setSelectedRow({
      title: 'Silver metric',
      entries: [
        ['Timestamp', fmt(m.timestamp)],
        ['Metric', m.metric],
        ['Value', String(m.value)],
        ['Unit', m.unit],
      ],
    })
  }
  function openCostRow(c: MyDeviceCost) {
    setSelectedRow({
      title: 'Gold metric',
      entries: [
        ['Period start', fmt(c.periodStart)],
        ['Period end', fmt(c.periodEnd)],
        ['Metric type', c.metricType],
        ['Value', c.value.toFixed(4)],
        ['Unit', c.unit],
      ],
    })
  }

  return (
    <div className="space-y-4">
      <Link to="/energy-os/dashboard" className="inline-flex items-center gap-1.5 text-xs text-ink-dim hover:text-ink transition-colors">
        <ArrowLeft size={13} /> Back to dashboard
      </Link>

      {device && (
        <section className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-ink">{device.label}</h1>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-sunken px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-dim">{device.deviceType}</span>
              </div>
              {device.subType && <p className="text-xs text-ink-dim mt-0.5">{device.subType}</p>}
            </div>
            <div className="flex items-center gap-2">
              {device.controllable && (
                <button
                  type="button"
                  disabled
                  title="Controls are coming in a future update"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink-dim border border-paper-border bg-white/70 cursor-not-allowed"
                >
                  <Power size={14} />
                </button>
              )}
              {vendorConfig?.supportsFileUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) uploadFile.mutate(file)
                      e.target.value = ''
                    }}
                  />
                  <Button
                    variant="secondary" size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    loading={uploadFile.isPending}
                    title="Upload a history file for this device"
                    icon={Upload}
                  >
                    Upload file
                  </Button>
                </>
              )}
              <Button variant="secondary" size="sm" onClick={() => setShowEdit(true)} icon={Pencil}>
                Edit
              </Button>
              <button
                onClick={() => reprocessHistory.mutate()}
                disabled={reprocessHistory.isPending}
                title="Recompute silver/gold data from this device's existing raw history"
                className="flex h-8 w-8 items-center justify-center rounded-full text-ink-dim border border-paper-border bg-white/70 hover:bg-paper-sunken transition-colors"
              >
                {reprocessHistory.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              </button>
              <button
                onClick={handleEraseHistory}
                disabled={eraseHistory.isPending}
                title="Permanently delete all raw, silver, and gold history for this device"
                className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 border border-red-200 bg-white/70 hover:bg-red-50 transition-colors"
              >
                {eraseHistory.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </div>
          </div>
          {uploadFile.isSuccess && !fileUploadError && (
            <p className="text-theme-xs text-emerald-600 mt-2">File received — new readings will appear here shortly once processed.</p>
          )}
          {fileUploadError && (
            <p className="text-theme-xs text-red-600 mt-2">{fileUploadError}</p>
          )}
          {reprocessHistory.isSuccess && !reprocessError && (
            <p className="text-theme-xs text-emerald-600 mt-2">
              Reprocessing started — this runs in the background and can take a while for a large history. Refresh this page later to see new readings.
            </p>
          )}
          {reprocessError && (
            <p className="text-theme-xs text-red-600 mt-2">{reprocessError}</p>
          )}
          {eraseHistory.isSuccess && !eraseError && (
            <p className="text-theme-xs text-emerald-600 mt-2">
              Erasing started — this runs in the background and can take a while for a large history. Refresh this page later to confirm it's gone.
            </p>
          )}
          {eraseError && (
            <p className="text-theme-xs text-red-600 mt-2">{eraseError}</p>
          )}
        </section>
      )}

      {device?.deviceType === 'meter' && <InvoicesSection />}

      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold text-ink">Device readings</h1>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {(['bar', 'line', 'area'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setChartMode(m)}
                className={`px-2.5 py-1 rounded-full text-theme-xs font-medium capitalize transition-colors ${chartMode === m ? 'bg-blue-100 text-blue-700' : 'text-ink-dim hover:text-ink-dim'}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setRange(opt.key)}
                className={`px-2.5 py-1 rounded-full text-theme-xs font-medium transition-colors ${range === opt.key ? 'bg-blue-100 text-blue-700' : 'text-ink-dim hover:text-ink-dim'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {AGGREGATION_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setAggregation(opt.key)}
                className={`px-2.5 py-1 rounded-full text-theme-xs font-medium transition-colors ${aggregation === opt.key ? 'bg-emerald-100 text-emerald-700' : 'text-ink-dim hover:text-ink-dim'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-5 flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-ink-dim" />
        </div>
      ) : allSeries.length === 0 ? (
        <section className="card p-5">
          <p className="text-xs text-ink-dim text-center py-10">No readings yet</p>
        </section>
      ) : (
        allSeries.map((s) => {
          const rows = aggregateSeries(visibleRows(s.rows), aggregation, s.unit)
          const unit = aggregatedUnit(s.unit, aggregation)
          const { data, segments } = toWideChartRows(rows, s.color)
          const hasSegments = segments.length > 1 || segments[0]?.key !== 'value'
          return (
            <section key={s.id} className="card p-5">
              <p className="text-theme-xs font-semibold text-ink-dim uppercase tracking-wide mb-2">{s.title} ({unit})</p>
              {data.length === 0 ? (
                <p className="text-xs text-ink-dim text-center py-16">No readings in this range</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={240}>
                    {chartMode === 'bar' ? (
                      <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => applyDateFormat(v, tickFormat(range, aggregation))}
                          minTickGap={40}
                        />
                        <YAxis tick={{ fontSize: 11 }} width={40} />
                        {/* Full exact date+time on hover, using the business's own configured
                            DATE_FORMAT — the axis ticks above stay short/zoom-appropriate. */}
                        <Tooltip labelFormatter={(v) => fmt(String(v))} formatter={(v: number, name: string) => [`${v} ${unit}`, name]} />
                        {segments.map((seg) => (
                          <Bar key={seg.key} dataKey={seg.key} name={seg.label || s.title} stackId="segments" fill={seg.color} radius={[2, 2, 0, 0]} isAnimationActive={false} />
                        ))}
                      </BarChart>
                    ) : chartMode === 'line' ? (
                      <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => applyDateFormat(v, tickFormat(range, aggregation))}
                          minTickGap={40}
                        />
                        <YAxis tick={{ fontSize: 11 }} width={40} />
                        <Tooltip labelFormatter={(v) => fmt(String(v))} formatter={(v: number, name: string) => [`${v} ${unit}`, name]} />
                        {segments.map((seg) => (
                          <Line
                            key={seg.key} type="monotone" dataKey={seg.key} name={seg.label || s.title} stroke={seg.color}
                            strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls={!hasSegments}
                            isAnimationActive={false}
                          />
                        ))}
                      </LineChart>
                    ) : (
                      <AreaChart data={data}>
                        <defs>
                          {segments.map((seg) => (
                            <linearGradient key={seg.key} id={`${s.id}-${seg.key}-fill`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={seg.color} stopOpacity={0.4} />
                              <stop offset="95%" stopColor={seg.color} stopOpacity={0.03} />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f2f4f7" vertical={false} />
                        <XAxis
                          dataKey="timestamp"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => applyDateFormat(v, tickFormat(range, aggregation))}
                          minTickGap={40}
                        />
                        <YAxis tick={{ fontSize: 11 }} width={40} />
                        <Tooltip labelFormatter={(v) => fmt(String(v))} formatter={(v: number, name: string) => [`${v} ${unit}`, name]} />
                        {segments.map((seg) => (
                          <Area
                            key={seg.key} type="monotone" dataKey={seg.key} name={seg.label || s.title} stroke={seg.color} strokeWidth={2}
                            fill={`url(#${s.id}-${seg.key}-fill)`} dot={false} activeDot={{ r: 4 }} connectNulls={!hasSegments}
                            isAnimationActive={false}
                          />
                        ))}
                      </AreaChart>
                    )}
                  </ResponsiveContainer>
                  {hasSegments && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 justify-center">
                      {segments.map((seg) => (
                        <div key={seg.key} className="flex items-center gap-1.5 text-theme-xs text-ink-dim">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: seg.color }} />
                          {seg.label}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>
          )
        })
      )}

      <section className="card p-5">
        <h2 className="text-sm font-semibold text-ink mb-4">Pipeline</h2>
        <div className="flex gap-1 mb-4">
          {(['bronze', 'silver', 'gold'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setPipelineTab(t)}
              className={`px-2.5 py-1 rounded border text-theme-xs capitalize transition-colors ${pipelineTab === t ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white/70 text-ink-dim border-white/80 hover:border-paper-border'}`}
            >
              {t}
            </button>
          ))}
        </div>
        <p className="text-theme-xs text-ink-dim mb-2">Click a row for full details</p>

        {pipelineTab === 'bronze' && (
          !rawReadings || rawReadings.length === 0 ? (
            <p className="text-xs text-ink-dim text-center py-8">No raw data yet</p>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto max-h-96 overflow-y-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Mode</th>
                    <th>Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {rawReadings.map((r, i) => (
                    <tr key={i} onClick={() => openRawRow(r)} className="cursor-pointer">
                      <td className="text-ink-dim">{fmt(r.receivedAt)}</td>
                      <td className="text-ink-dim">{r.ingestMode}</td>
                      <td className="text-ink-dim font-mono text-theme-xs max-w-md truncate">{r.rawPayload}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {pipelineTab === 'silver' && (
          !metrics || metrics.length === 0 ? (
            <p className="text-xs text-ink-dim text-center py-8">No metrics yet</p>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto max-h-96 overflow-y-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {[...metrics].reverse().map((m, i) => (
                    <tr key={i} onClick={() => openMetricRow(m)} className="cursor-pointer">
                      <td className="text-ink-dim">{fmt(m.timestamp)}</td>
                      <td className="text-ink-dim">{m.metric}</td>
                      <td className="font-medium">{m.value}</td>
                      <td className="text-ink-dim">{m.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {pipelineTab === 'gold' && (
          !costs || costs.length === 0 ? (
            <p className="text-xs text-ink-dim text-center py-8">No derived metrics yet</p>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white overflow-x-auto max-h-96 overflow-y-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Metric</th>
                    <th>Value</th>
                    <th>Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {[...costs].reverse().map((c, i) => (
                    <tr key={i} onClick={() => openCostRow(c)} className="cursor-pointer">
                      <td className="text-ink-dim">{fmt(c.periodStart)} → {fmt(c.periodEnd)}</td>
                      <td className="text-ink-dim">{c.metricType}</td>
                      <td className="font-medium">{c.value.toFixed(4)}</td>
                      <td className="text-ink-dim">{c.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>

      {selectedRow && <RowDetailModal row={selectedRow} onClose={() => setSelectedRow(null)} />}

      {showEdit && device && (
        <EditDeviceModal
          device={device}
          vendors={vendors ?? []}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); queryClient.invalidateQueries({ queryKey: ['my-home'] }) }}
        />
      )}
    </div>
  )
}

// periodStart/periodEnd are plain 'YYYY-MM-DD' dates, not timestamps — same reasoning as
// EnergyOSHomeDetail.tsx's own fmtDateOnly: showing them via the business's full DATE_FORMAT
// would be noisy, and re-parsing through `new Date()` risks a day shift depending on the
// viewer's timezone. Just show the digits the server sent.
function fmtDateOnly(d: string): string {
  return d.length >= 10 ? d.slice(0, 10) : d
}

// Replaces the old bare "Cost of usage" rate card for meters (Phase 9) — a €/h instantaneous rate
// wasn't something a homeowner could act on, whereas a real bill (past, persisted) plus a live
// current-month estimate is. Renders nothing (not even a placeholder) when the home has neither a
// contract nor a designated meter yet, matching how the old card also only appeared once there was
// something to show.
function InvoicesSection() {
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['my-invoices'],
    queryFn: () => getMyInvoices().then((r) => r.data),
  })

  if (isLoading) {
    return (
      <section className="card p-5 flex items-center justify-center py-10">
        <Loader2 size={18} className="animate-spin text-ink-dim" />
      </section>
    )
  }
  if (!data || (!data.current && data.past.length === 0)) return null

  return (
    <section className="card p-5 space-y-4">
      <h2 className="text-sm font-semibold text-ink">Invoices</h2>

      {data.current && (
        <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-4">
          <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-1">Current bill (in progress)</p>
          <p className="text-xl font-semibold text-ink">{data.current.estimatedTotal.toFixed(2)} €</p>
          <p className="text-theme-xs text-ink-dim mt-1">
            {data.current.consumptionKwhSoFar.toFixed(1)} kWh so far since {fmtDateOnly(data.current.periodStart)} · {data.current.consumptionCostSoFar.toFixed(2)}€ consumption + {data.current.monthlyFeesCost.toFixed(2)}€ fees
          </p>
        </div>
      )}

      {data.past.length > 0 && (
        <div className="space-y-1.5">
          {data.past.map((inv) => {
            const isOpen = openInvoiceId === inv.id
            return (
              <div key={inv.id} className="border border-paper-border rounded-lg overflow-hidden">
                <button
                  onClick={() => setOpenInvoiceId(isOpen ? null : inv.id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/50 transition-colors"
                >
                  <span className="text-sm text-ink">{fmtDateOnly(inv.periodStart)} → {fmtDateOnly(inv.periodEnd)}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink">{inv.totalCost.toFixed(2)} €</span>
                    {isOpen ? <ChevronUp size={14} className="text-ink-dim" /> : <ChevronDown size={14} className="text-ink-dim" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-3 space-y-1 text-theme-xs text-ink-dim">
                    <div className="flex justify-between"><span>Consumption</span><span>{inv.consumptionKwh.toFixed(1)} kWh · {inv.consumptionCost.toFixed(2)} €</span></div>
                    <div className="flex justify-between"><span>Monthly fees</span><span>{inv.monthlyFeesCost.toFixed(2)} €</span></div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function RowDetailModal({ row, onClose }: { row: NonNullable<SelectedRow>; onClose: () => void }) {
  let prettyPayload: string | undefined
  if (row.rawPayload !== undefined) {
    try { prettyPayload = JSON.stringify(JSON.parse(row.rawPayload), null, 2) }
    catch { prettyPayload = row.rawPayload }
  }

  return (
    <Modal title={row.title} onClose={onClose}>
      <div className="space-y-3">
        {row.entries.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-3 text-sm border-b border-paper-border pb-2 last:border-0">
            <span className="text-ink-dim">{label}</span>
            <span className="text-ink font-medium text-right break-all">{value}</span>
          </div>
        ))}
        {prettyPayload !== undefined && (
          <div>
            <p className="text-theme-xs font-semibold text-ink-dim uppercase tracking-wide mb-1">Payload</p>
            <pre className="text-theme-xs text-ink bg-paper-sunken border border-paper-border rounded p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-80">
              {prettyPayload}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  )
}

function EditDeviceModal({ device, vendors, onClose, onSaved }: { device: MyDevice; vendors: MyVendor[]; onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState(device.label)
  const [subType, setSubType] = useState(device.subType ?? '')
  const [controllable, setControllable] = useState(device.controllable)
  const [vendor, setVendor] = useState(device.vendor)
  const [simulationEnabled, setSimulationEnabled] = useState(device.simulationEnabled)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)

  const handleSubmit = async () => {
    if (!label.trim()) { setError('Name is required'); return }
    setError(''); setPending(true)
    try {
      await updateMyDevice(device.deviceCoffeeId, {
        label: label.trim(), subType: subType.trim() || undefined, controllable,
        vendor: vendor !== device.vendor ? vendor : undefined,
        simulationEnabled: simulationEnabled !== device.simulationEnabled ? simulationEnabled : undefined,
      })
      onSaved()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update device')
    } finally {
      setPending(false)
    }
  }

  return (
    <Modal title="Edit device" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Name">
          <input value={label} onChange={(e) => setLabel(e.target.value)} className="field" />
        </Field>
        <Field label="Sub type">
          <input value={subType} onChange={(e) => setSubType(e.target.value)} placeholder="Fridge" className="field" />
        </Field>
        <Field label="Vendor">
          <select value={vendor} onChange={(e) => setVendor(e.target.value)} className="field">
            {(vendors.some((v) => v.name === vendor) ? vendors : [{ name: vendor, label: vendor }, ...vendors]).map((v) => (
              <option key={v.name} value={v.name}>{v.label}</option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input type="checkbox" checked={controllable} onChange={(e) => setControllable(e.target.checked)} className="rounded border-paper-border" />
          Allow controls
        </label>
        {vendor === 'mock' && (
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input type="checkbox" checked={!simulationEnabled} onChange={(e) => setSimulationEnabled(!e.target.checked)} className="rounded border-paper-border" />
            Pause simulated data
          </label>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button onClick={handleSubmit} disabled={pending} className="w-full">
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  )
}
